// src/pages/CapturePage/blocks/StoryTime.js
//
// A conversation sample: a picture, and the child telling what is happening.
//
// Two samples, not one. The therapist asked for both: a **story task**, which is
// the same for every child and therefore makes two children comparable, and
// **free conversation**, which shows how the child actually speaks. Each answers
// what the other cannot.
//
// This block goes down the ordinary speech-recognition path — it is transcribed,
// not measured — so it records the way the practice flow does, with the filters
// that help recognition left on.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import BlockExit from '../BlockExit';
import { captureError, saveVoiceSample } from '../captureApi';
import { blobToBase64 } from '../rawRecorder';

const PROMPT_IMAGES = ['🏞️', '🎪', '🏫', '🍲'];

export default function StoryTime({ session, block, onSaved, onFinish }) {
    const tasks = block.tasks || [{ key: 'story', en: 'Picture or story task', hi: 'तस्वीर से कहानी' }];
    const done = new Set(
        (session.voice || [])
            .filter((v) => v.kind === 'conversation' && v.conversation_task)
            .map((v) => v.conversation_task)
    );
    const [taskIndex, setTaskIndex] = useState(() => {
        const next = tasks.findIndex((t) => !done.has(t.key));
        return next === -1 ? 0 : next;
    });
    const task = tasks[taskIndex];

    const [phase, setPhase] = useState('ready');
    const [elapsed, setElapsed] = useState(0);
    const [error, setError] = useState('');
    const [picture] = useState(() => PROMPT_IMAGES[Math.floor(Math.random() * PROMPT_IMAGES.length)]);
    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const tickRef = useRef(null);

    const clearTick = () => {
        if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    };
    useEffect(() => clearTick, []);

    const upload = useCallback(async (blob, durationMs) => {
        setPhase('saving');
        try {
            const base64 = await blobToBase64(blob);
            const r = await saveVoiceSample(session.id, {
                kind: 'conversation',
                conversation_task: task.key,
                audio_base64: base64,
                duration_ms: durationMs,
            });
            onSaved(r.data);
            setPhase('ready');
            setElapsed(0);
            // On to the other task; the block ends once both are recorded.
            if (taskIndex + 1 < tasks.length) setTaskIndex(taskIndex + 1);
            else onFinish();
        } catch (e) {
            setError(captureError(e, 'Could not save the story. Try recording it again.'));
            setPhase('ready');
        }
    }, [onFinish, onSaved, session.id, task, taskIndex, tasks.length]);

    const stop = useCallback(() => {
        clearTick();
        const recorder = recorderRef.current;
        if (recorder && recorder.state !== 'inactive') recorder.stop();
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    }, []);

    const start = async () => {
        setError('');
        try {
            // Same constraints as the practice flow: this sample is for
            // transcription, where the filters help rather than hurt.
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true },
            });
            streamRef.current = stream;
            const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
            recorderRef.current = recorder;
            chunksRef.current = [];
            const startedAt = Date.now();

            recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            recorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                upload(blob, Date.now() - startedAt);
            };
            recorder.start();
            setPhase('recording');

            const max = block.max_seconds || 240;
            tickRef.current = setInterval(() => {
                const seconds = Math.floor((Date.now() - startedAt) / 1000);
                setElapsed(seconds);
                if (seconds >= max) stop();
            }, 500);
        } catch (e) {
            setError('The microphone is not available. Check the permission and try again.');
        }
    };

    const target = block.target_seconds || 120;

    return (
        <div className="cap-child-screen">
            <div className="cap-child-head">
                <span className="cap-child-step">{taskIndex + 1} / {tasks.length}</span>
                <h2>{block.child_title?.hi}</h2>
                <p>{block.child_title?.en}</p>
            </div>

            {/* The free-conversation half needs no picture: a picture turns it
                back into a set task. */}
            {task.key === 'free' ? (
                <div className="cap-picture" role="img" aria-label="talking">💬</div>
            ) : (
                <div className="cap-picture" role="img" aria-label="story picture">{picture}</div>
            )}

            <p className="cap-instruction">
                {task.key === 'free'
                    ? <>अब अपनी बात बताओ — स्कूल, घर, खेल। <em>Now tell me about your day.</em></>
                    : <>{block.instruction?.hi} <em>{block.instruction?.en}</em></>}
            </p>

            {phase === 'recording' ? (
                <>
                    <p className="cap-big">{Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}</p>
                    <div className="cap-progress">
                        <span style={{ width: `${Math.min(100, (elapsed / target) * 100)}%` }} />
                    </div>
                </>
            ) : null}

            {error ? <p className="cap-error">{error}</p> : null}

            {phase === 'ready' ? (
                <div className="cap-actions">
                    <button type="button" className="cap-btn big" onClick={start}>Start</button>
                    <button
                        type="button" className="cap-btn ghost"
                        onClick={() => (taskIndex + 1 < tasks.length ? setTaskIndex(taskIndex + 1) : onFinish())}
                    >
                        Skip this one
                    </button>
                </div>
            ) : null}
            {phase === 'saving' ? <p className="cap-hint">Saving…</p> : null}
            {phase === 'recording' ? <BlockExit onExit={stop} label="Hold to stop" /> : null}
        </div>
    );
}
